// src/hooks/useVideoRoom.ts
import { useState, useRef, useEffect, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { useAuth } from "../firebase/AuthProvider";
import { useNavigate, useParams } from "react-router-dom";
import {
    AcceptVideoCallAsync,
    InitiateVideoCallNotifyAsync,
    getHangupToken,
    getPublishers,
    getTurnCredentials,
    hangup,
    hangupScreen,
    hangupWithBeacon,
    keepAlive,
    sendOffer,
    sendTrickleCandidate,
    sendTrickleCompleted,
    subscribeToFeed,
} from "../services/videoService/VideoService";
import { NetworkQuality } from "../interfaces/video/video";
import { ValidateOtpResponseDto } from "../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import { User } from "firebase/auth";
import {
    CallActionRequest,
    InitiateCallRequest,
} from "../interfaces/videocallDtos/videoCall";
import { toast } from "react-toastify";

interface Publisher {
    feedId: string;
    display: string;
    userId?: string;
    isMuted?: boolean;
    isCameraOn?: boolean;
}

interface RemoteFeed {
    feedId: string;
    displayName?: string;
    userId?: string;
    stream: MediaStream | null;
    isMuted: boolean;
    isCameraOn: boolean;
    connectionState?: RTCPeerConnectionState;
}

type RemoteFeedState = Record<string, RemoteFeed>;

export const useVideoRoom = (
    isPublisher: boolean,
    relatedChatConversationId: number | undefined,
    calenderEventId: string | null,
    isOneToOneConversation: boolean = false,
    initialDisplayName: string,
    initialVideoDevice?: string,
    initialAudioDevice?: string,
    externalUserDetails?: ValidateOtpResponseDto | null,
    initialIsMuted: boolean = false,
    initialIsCameraOn: boolean = false
) => {
    const { currentUser, currentUserInfo } = useAuth();
    const { roomId } = useParams<{ roomId: string }>();
    const navigate = useNavigate();

    const [janusSessionId, setJanusSessionId] = useState<number | null>(null);
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [remoteFeeds, setRemoteFeeds] = useState<RemoteFeedState>({});
    const [isMuted, setIsMuted] = useState(initialIsMuted);
    const [isCameraOn, setIsCameraOn] = useState(initialIsCameraOn);
    const [isSharingScreen, setIsSharingScreen] = useState(false);
    const [activeScreenShare, setActiveScreenShare] = useState<{
        feedId: string;
        stream: MediaStream | null;
        userId?: string;
        displayName?: string;
    } | null>(null);
    const [selectedVideoDevice, setSelectedVideoDevice] = useState<
        string | undefined
    >(initialVideoDevice);
    const [selectedAudioDevice, setSelectedAudioDevice] = useState<
        string | undefined
    >(initialAudioDevice);
    const [networkQuality, setNetworkQuality] =
        useState<NetworkQuality>("unknown");
    const [latency, setLatency] = useState<number>(-1);


    const iceServersRef = useRef<RTCIceServer[]>([]);
    const localStreamRef = useRef<MediaStream | null>(null);
    const publisherPcRef = useRef<RTCPeerConnection | null>(null);
    const videoTransceiverRef = useRef<RTCRtpTransceiver | null>(null);
    const remotePcsRef = useRef<Record<string, RTCPeerConnection>>({});
    const hubConnectionRef = useRef<signalR.HubConnection | null>(null);
    const janusSessionRef = useRef<{
        sessionId: number | null;
        handleId: number | null;
        feedId: string | null;
    }>({ sessionId: null, handleId: null, feedId: null });
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const statsIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const screenSharePublisherPcRef = useRef<RTCPeerConnection | null>(null);
    const screenShareStreamRef = useRef<MediaStream | null>(null);
    const janusScreenShareHandleRef = useRef<{
        handleId: number | null;
        feedId: string | null;
        sessionId: number | null;
    }>({ handleId: null, feedId: null, sessionId: null });
    const hangupTokenRef = useRef<string | null>(null); // <-- NEW: Ref for the main hangup token
    const screenShareHangupTokenRef = useRef<string | null>(null); // <-- NEW: Ref for the screen share hangup token
    const refreshSessionIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const activeScreenShareRef = useRef(activeScreenShare);
    const disconnectionTimersRef = useRef<Record<string, NodeJS.Timeout>>({});
    const remoteFeedsRef = useRef(remoteFeeds);
    // Prevent re-entrant toggles (double-click) while camera toggle is in progress
    const isTogglingCameraRef = useRef(false);

    useEffect(() => {
        remoteFeedsRef.current = remoteFeeds;
    }, [remoteFeeds]);

    useEffect(() => {
        activeScreenShareRef.current = activeScreenShare;
    }, [activeScreenShare]);

    const cleanup = useCallback(async () => {
        console.log("Running cleanup...");

        // Stop all intervals
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
        if (refreshSessionIntervalRef.current) clearInterval(refreshSessionIntervalRef.current);
        Object.values(disconnectionTimersRef.current).forEach(clearTimeout);
        pollingIntervalRef.current = null;
        statsIntervalRef.current = null;
        refreshSessionIntervalRef.current = null;
        disconnectionTimersRef.current = {};

        // Stop all media tracks
        localStreamRef.current?.getTracks().forEach((track) => track.stop());
        screenShareStreamRef.current?.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
        screenShareStreamRef.current = null;

        // Close all peer connections
        publisherPcRef.current?.close();
        screenSharePublisherPcRef.current?.close();
        Object.values(remotePcsRef.current).forEach((pc) => pc.close());
        publisherPcRef.current = null;
        videoTransceiverRef.current = null;
        screenSharePublisherPcRef.current = null;
        remotePcsRef.current = {};

        // Stop SignalR connection
        if (hubConnectionRef.current) {
            try {
                const connectionState = hubConnectionRef.current.state;
                if (connectionState === signalR.HubConnectionState.Connected || 
                    connectionState === signalR.HubConnectionState.Connecting ||
                    connectionState === signalR.HubConnectionState.Reconnecting) {
                    await hubConnectionRef.current.stop();
                }
            } catch (error: any) {
                // Ignore errors when stopping connection - connection may already be closed
                if (error?.message?.includes("connection being closed") || 
                    error?.message?.includes("Invocation canceled") ||
                    error?.message?.includes("underlying connection")) {
                    console.log("SignalR connection already closed during cleanup");
                } else {
                    console.error("Error stopping SignalR connection:", error);
                }
            } finally {
                hubConnectionRef.current = null;
            }
        }

        // Reset Janus refs
        janusSessionRef.current = { sessionId: null, handleId: null, feedId: null };
        janusScreenShareHandleRef.current = { sessionId: null, handleId: null, feedId: null };

        console.log("Cleanup complete.");
    }, []);

    const cleanupRemoteFeed = useCallback((feedId: string) => {
        if (remotePcsRef.current[feedId]) {
            remotePcsRef.current[feedId].close();
            delete remotePcsRef.current[feedId];
        }
        if (disconnectionTimersRef.current[feedId]) {
            clearTimeout(disconnectionTimersRef.current[feedId]);
            delete disconnectionTimersRef.current[feedId];
        }
        setRemoteFeeds((prev) => {
            const newFeeds = { ...prev };
            const feedToRemove = newFeeds[feedId];
            if (feedToRemove?.stream) {
                feedToRemove.stream.getTracks().forEach((track) => track.stop());
            }
            delete newFeeds[feedId];
            return newFeeds;
        });
    }, []);

    const checkNetworkStats = useCallback(async () => {
        if (!publisherPcRef.current) return;
        try {
            const stats = await publisherPcRef.current.getStats();
            let roundTripTime = -1;
            stats.forEach((report) => {
                if (
                    report.type === "candidate-pair" &&
                    report.state === "succeeded" &&
                    report.currentRoundTripTime
                ) {
                    roundTripTime = Math.round(report.currentRoundTripTime * 1000);
                }
            });
            setLatency(roundTripTime);
            if (roundTripTime < 0) setNetworkQuality("unknown");
            else if (roundTripTime < 250) setNetworkQuality("good");
            else if (roundTripTime < 500) setNetworkQuality("average");
            else setNetworkQuality("poor");
        } catch (error) {
            console.error("Error getting network stats:", error);
            setNetworkQuality("unknown");
            setLatency(-1);
        }
    }, []);

    const subscribeToRemoteFeed = useCallback(
        async (publisher: Publisher, sessionId: number, isRetry: boolean = false) => {
            const {
                feedId,
                display,
                userId,
                isMuted = false,
                isCameraOn = true,
            } = publisher;

            if (
                (!currentUser && !externalUserDetails) ||
                !sessionId ||
                remotePcsRef.current[feedId]
            )
                return;

            const token = currentUser ? await currentUser.getIdToken() : "";

            // Add a UI placeholder for the incoming feed early so the UI doesn't flicker/skip the tile
            setRemoteFeeds(prev => {
                if (prev[feedId]) return prev;
                return {
                    ...prev,
                    [feedId]: {
                        feedId,
                        displayName: display,
                        userId,
                        stream: null,
                        isMuted: isMuted,
                        isCameraOn: isCameraOn,
                    }
                };
            });

            const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
            remotePcsRef.current[feedId] = pc;

            pc.onconnectionstatechange = () => {
                setRemoteFeeds((prev) => {
                    if (!prev[feedId]) return prev;
                    return {
                        ...prev,
                        [feedId]: { ...prev[feedId], connectionState: pc.connectionState },
                    };
                });
                console.log(
                    `Remote PC for feed ${feedId} connection state: ${pc.connectionState}`
                );
                // Clear any existing timer for this feed
                if (disconnectionTimersRef.current[feedId]) {
                    clearTimeout(disconnectionTimersRef.current[feedId]);
                    delete disconnectionTimersRef.current[feedId];
                }

                if (pc.connectionState === 'disconnected') {
                    console.warn(`Remote PC for feed ${feedId} is disconnected. Will remove in 5 seconds if not reconnected.`);
                    disconnectionTimersRef.current[feedId] = setTimeout(() => {
                        const currentPc = remotePcsRef.current[feedId];
                        if (currentPc && (currentPc.connectionState === 'disconnected' || currentPc.connectionState === 'failed')) {
                            console.log(`Removing disconnected/failed feed ${feedId} after timeout.`);
                            cleanupRemoteFeed(feedId);
                        }
                        delete disconnectionTimersRef.current[feedId];
                    }, 5000);  // 5-second timeout
                } else if (["failed", "closed"].includes(pc.connectionState)) {
                    console.warn(`Remote PC for feed ${feedId} ${pc.connectionState}. Cleaning up.`);
                    cleanupRemoteFeed(feedId);
                }
            };

            // ontrack: prefer provided streams[0], otherwise build one from track(s)
            pc.ontrack = (event) => {
                try {
                    const incoming =
                        event.streams && event.streams.length > 0
                            ? event.streams[0]
                            : new MediaStream(event.track ? [event.track] : []);

                    // Ensure we provide a fresh MediaStream object to trigger React updates
                    const streamToSet = new MediaStream(incoming.getTracks());

                    setRemoteFeeds((prev) => ({
                        ...prev,
                        [feedId]: {
                            ...prev[feedId],
                            stream: streamToSet,
                            isMuted: prev[feedId]?.isMuted ?? isMuted,
                            isCameraOn: prev[feedId]?.isCameraOn ?? isCameraOn,
                        },
                    }));
                } catch (err) {
                    console.error(`Error handling ontrack for feed ${feedId}:`, err);
                }
            };

            try {
                // Request an offer from server (Janus attach/join/offer -> returns subscriber offer data)
                const res = await subscribeToFeed(token, {
                    sessionId,
                    roomId,
                    feedId: publisher.feedId,
                });
                if (!res?.receivedOffer || !res.subscriberHandleId)
                    throw new Error("Invalid subscribe response.");

                const subscriberHandleId = res.subscriberHandleId;

                pc.onicecandidate = (e) => {
                    if (e.candidate)
                        sendTrickleCandidate(
                            sessionId,
                            subscriberHandleId,
                            e.candidate,
                            token
                        );
                };

                pc.onicegatheringstatechange = () => {
                    if (pc.iceGatheringState === "complete") {
                        sendTrickleCompleted(sessionId, subscriberHandleId, token);
                    }
                };

                // Try to set remote description — if PC was closed (race), treat as transient and retry once
                try {
                    // defensive: if pc already closed, throw to go to outer catch and retry logic
                    if (pc.signalingState === "closed" || pc.connectionState === "closed") {
                        throw new Error("PeerConnection closed before setRemoteDescription");
                    }
                    await pc.setRemoteDescription(JSON.parse(res.receivedOffer));
                } catch (err: any) {
                    console.error(`setRemoteDescription failed for feed ${feedId}:`, err);
                    // ensure this pc is cleaned up
                    try { pc.close(); } catch { }
                    delete remotePcsRef.current[feedId];

                    // Retry once if not already retried
                    if (!isRetry) {
                        console.log(`Retrying subscription for feed ${feedId} in 1s due to closed PC...`);
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        const freshPublishers = await getPublishers(token, roomId);
                        const publisherStillExists = freshPublishers.some(p => p.feedId === feedId);
                        if (publisherStillExists) {
                            // call again with isRetry to avoid infinite loop
                            subscribeToRemoteFeed(publisher, sessionId, true);
                        } else {
                            console.log(`Publisher ${feedId} no longer exists. Aborting retry.`);
                            // cleanup UI placeholder (feed gone)
                            cleanupRemoteFeed(feedId);
                        }
                    } else {
                        // final failure after retry; remove placeholder
                        cleanupRemoteFeed(feedId);
                    }
                    return;
                }

                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);

                await subscribeToFeed(token, {
                    sessionId,
                    handleId: subscriberHandleId,
                    roomId,
                    feedId,
                    sdpOffer: answer.sdp!,
                });
            } catch (error: any) {
                console.error(`Failed to subscribe to feed ${feedId}:`, error);
                try { pc.close(); } catch { }
                delete remotePcsRef.current[feedId];

                if (!isRetry) {
                    console.log(`Retrying subscription for feed ${feedId} in 3 seconds...`);
                    await new Promise(resolve => setTimeout(resolve, 3000));
                    const freshPublishers = await getPublishers(token, roomId);
                    const publisherStillExists = freshPublishers.some(p => p.feedId === feedId);
                    if (publisherStillExists) {
                        console.log(`Publisher ${feedId} still exists. Retrying now.`);
                        subscribeToRemoteFeed(publisher, sessionId, true);
                    } else {
                        console.log(`Publisher ${feedId} no longer exists. Aborting retry.`);
                        cleanupRemoteFeed(feedId);
                    }
                } else {
                    cleanupRemoteFeed(feedId);
                }
            }
        },
        [currentUser, externalUserDetails, roomId, cleanupRemoteFeed]
    );

    // in useVideoRoom.ts

    const keepAliveAsync = async () => {
        const token = await currentUser?.getIdToken();
        if (!janusSessionRef.current.sessionId || !roomId || !token) return;
        keepAlive(janusSessionRef.current.sessionId, token).then((res)=>{
            console.log("Keep alive rung");
            
        }).catch((err)=>{
            console.log("Keep alive error ", err);
            
        })
    }

    const syncParticipants = useCallback(async () => {
        if (!janusSessionRef.current.sessionId || !roomId) return;

        const token = currentUser ? await currentUser.getIdToken() : "";
        try {
            const publishers = await getPublishers(token, roomId);
            const publisherFeedIds = new Set(publishers.map(p => p.feedId));
            const remoteFeedIds = Object.keys(remoteFeedsRef.current);

            // Add or update participants
            for (const publisher of publishers) {
                if (publisher.feedId === janusSessionRef.current.feedId) continue; // Skip self

                if (!remotePcsRef.current[publisher.feedId]) {
                    console.log(`[Polling] Found new publisher: ${publisher.display}, subscribing...`);
                    subscribeToRemoteFeed(publisher, janusSessionRef.current.sessionId);
                } else {
                    // Also sync media state
                    setRemoteFeeds(prev => {
                        if (prev[publisher.feedId] && (prev[publisher.feedId].isMuted !== publisher.isMuted || prev[publisher.feedId].isCameraOn !== publisher.isCameraOn)) {
                            return {
                                ...prev,
                                [publisher.feedId]: {
                                    ...prev[publisher.feedId],
                                    isMuted: publisher.isMuted ?? false,
                                    isCameraOn: publisher.isCameraOn ?? true,
                                }
                            };
                        }
                        return prev;
                    });
                }
            }

            // Remove stale participants
            for (const feedId of remoteFeedIds) {
                if (!publisherFeedIds.has(feedId)) {
                    console.log(`[Polling] Found stale feed: ${feedId}, cleaning up...`);
                    cleanupRemoteFeed(feedId);
                }
            }

            // Sync screen share state
            const screenSharePublisher = publishers.find(p => p.display?.endsWith('(Screen)'));
            if (screenSharePublisher) {
                if (activeScreenShareRef.current?.feedId !== screenSharePublisher.feedId) {
                    console.log(`[Polling] Setting active screen share to ${screenSharePublisher.feedId}`);
                    setActiveScreenShare({ feedId: screenSharePublisher.feedId, stream: null, userId: screenSharePublisher.userId, displayName: screenSharePublisher.display });
                    setIsSharingScreen(true);
                }
            } else {
                if (activeScreenShareRef.current) {
                    console.log(`[Polling] Clearing active screen share because it's no longer published.`);
                    // The cleanupRemoteFeed for the stale feed will have already been called above.
                    // We just need to clear the UI state.
                    setActiveScreenShare(null);
                    setIsSharingScreen(false);
                }
            }

        } catch (error) {
            console.error("[Polling] Error syncing participants:", error);
        }
    }, [currentUser, roomId, subscribeToRemoteFeed, cleanupRemoteFeed]);

    const startVideoRoom = useCallback(async () => {
        if (((!currentUser && !currentUserInfo) && !externalUserDetails) || !roomId) return;

        // Reset states for clean re-initialization
        setLocalStream(null);
        setRemoteFeeds({});
        setIsMuted(initialIsMuted);
        setIsCameraOn(initialIsCameraOn);
        setIsSharingScreen(false);
        setActiveScreenShare(null);
        setSelectedVideoDevice(initialVideoDevice);
        setSelectedAudioDevice(initialAudioDevice);

        // Clean up any previous connections or intervals
        publisherPcRef.current?.close();
        Object.values(remotePcsRef.current).forEach((pc) => pc.close());
        remotePcsRef.current = {};
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
        if (hubConnectionRef.current) {
            try {
                await hubConnectionRef.current.stop();
            } catch (error: any) {
                // Ignore errors when stopping connection - connection may already be closed
                if (error?.message?.includes("connection being closed") || 
                    error?.message?.includes("Invocation canceled") ||
                    error?.message?.includes("underlying connection")) {
                    console.log("SignalR connection already closed during startVideoRoom cleanup");
                } else {
                    console.error("Error stopping SignalR connection in startVideoRoom:", error);
                }
            }
        }

        // --- PHASE 1: SIGNALR and PRESENCE ---
        const tokenforHub = currentUser ? await currentUser.getIdToken() : "";
        const hubURL = externalUserDetails?.tempSessionToken
            ? `${process.env.REACT_APP_API_BASE_URL}/videoHub?videoToken=${externalUserDetails.tempSessionToken}&ticketId=${externalUserDetails.ticketId}&userId=${externalUserDetails.externalUserDetailId}`
            : `${process.env.REACT_APP_API_BASE_URL}/videoHub`;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubURL, { accessTokenFactory: () => tokenforHub })
            .withAutomaticReconnect()
            .build();
        hubConnectionRef.current = connection;

        connection.on("UserJoined", (user: { userId: string, displayName: string, isMuted: boolean, isCameraOn: boolean }) => {
            console.log("UserJoined received:", user);
            setRemoteFeeds((prev) => {
                const existing = Object.values(prev).find(p => p.userId === user.userId);
                if (existing) return prev; // Already know about this user

                const presenceFeedId = `presence-${user.userId}`;
                return {
                    ...prev,
                    [presenceFeedId]: {
                        feedId: presenceFeedId,
                        userId: user.userId,
                        displayName: user.displayName,
                        stream: null,
                        isMuted: user.isMuted,
                        isCameraOn: user.isCameraOn,
                    }
                };
            });
        });

        connection.on("ExistingParticipants", (participants: any[]) => {
            console.log("ExistingParticipants received:", participants);
            const newFeeds: RemoteFeedState = {};
            for (const p of participants) {
                // Don't add self to remote feeds
                if (p.userId === currentUser?.uid) continue;

                const feedId = p.feedId || `presence-${p.userId}`;
                newFeeds[feedId] = {
                    feedId: feedId,
                    userId: p.userId,
                    displayName: p.display,
                    stream: null,
                    isMuted: p.isMuted,
                    isCameraOn: p.isCameraOn,
                    connectionState: p.feedId ? 'new' : undefined
                };
            }
            setRemoteFeeds(prev => ({ ...prev, ...newFeeds }));
        });

        connection.on("NewPublisher", (publisher: Publisher) => {
            console.log("NewPublisher received:", publisher);
            setRemoteFeeds((prev) => {
                const newFeeds = { ...prev };
                const oldFeedId = `presence-${publisher.userId}`;
                if (newFeeds[oldFeedId]) {
                    delete newFeeds[oldFeedId];
                }
                newFeeds[publisher.feedId] = {
                    feedId: publisher.feedId,
                    userId: publisher.userId,
                    displayName: publisher.display,
                    stream: null,
                    isMuted: publisher.isMuted ?? false,
                    isCameraOn: publisher.isCameraOn ?? true,
                };
                return newFeeds;
            });

            const currentSessionId = janusSessionRef.current.sessionId;
            if (currentSessionId) {
                subscribeToRemoteFeed(publisher, currentSessionId);
            }
        });

        connection.on("UserLeft", (data: { roomId: string, userId: string }) => {
            const feedIdToRemove = Object.values(remoteFeeds).find(
                (feed) => feed.userId === data.userId
            )?.feedId;

            if (feedIdToRemove) {
                console.log(`User ${data.userId} left, cleaning up feed ${feedIdToRemove}`);
                cleanupRemoteFeed(feedIdToRemove);
            }
        });

        connection.on(
            "RemoteMediaStateChanged",
            (data: { feedId: string; isMuted: boolean; isCameraOn: boolean }) => {
                setRemoteFeeds((prev) => {
                    if (!prev[data.feedId]) return prev;
                    return {
                        ...prev,
                        [data.feedId]: {
                            ...prev[data.feedId],
                            isMuted: data.isMuted,
                            isCameraOn: data.isCameraOn,
                        },
                    };
                });
            }
        );

        connection.on("RemoteFeedLeft", (data: { feedId: string }) => {
            cleanupRemoteFeed(data.feedId);
        });

        connection.on(
            "ScreenShareStarted",
            (data: { userId: string; feedId: string }) => {
                setActiveScreenShare({ feedId: data.feedId, stream: null, userId: data.userId });
                setIsSharingScreen(true);
            }
        );
        connection.on("ScreenShareEnded", (data: { feedId: string }) => {
            if (data?.feedId) {
                cleanupRemoteFeed(data.feedId);
            }
            if (activeScreenShareRef.current?.feedId === data?.feedId) {
                setActiveScreenShare(null);
                setIsSharingScreen(false);
            }
        });
        connection.on(
            "InitialScreenShareState",
            (data: { userId: string; feedId: string }) => {
                setActiveScreenShare({ feedId: data.feedId, stream: null, userId: data.userId });
                setIsSharingScreen(true);
            }
        );
        connection.on("EndRoomOnLeftLastSecondUser", (payload) => {
            if (payload?.roomId === roomId) {
                toast.success("Call Ended");
                handleHangUp();
            }
        });
        if (isPublisher && isOneToOneConversation) {
            connection.on("CallRejected", (data) => {
                toast.warn("Call Reject");
                handleHangUp(true);
            });
        }

        try {
            await connection.start();
            console.log("SignalR connected, connectionId=", connection.connectionId);
        } catch (err) {
            console.error("SignalR connection failed:", err);
            // bail out — don't continue to JoinRoom/publish if the hub couldn't connect
            return;
        }
        await connection.invoke("JoinRoom", roomId);
        const publish = async () => {
            try {
                const token = currentUser ? await currentUser.getIdToken() : "";
                const servers = await getTurnCredentials(token);
                iceServersRef.current = [
                    { urls: "stun:stun.l.google.com:19302" },
                    ...(servers ?? []),
                ];

                const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
                publisherPcRef.current = pc;

                // Set up transceivers BEFORE getting media, and store them
                const audioTransceiver = pc.addTransceiver('audio', { direction: 'sendonly' });
                videoTransceiverRef.current = pc.addTransceiver('video', { direction: 'sendonly' });

                pc.onconnectionstatechange = () => { /* ... existing logic ... */ };

                const videoConstraint = initialIsCameraOn
                    ? (selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true)
                    : false;

                const stream = await navigator.mediaDevices.getUserMedia({
                    video: videoConstraint,
                    audio: selectedVideoDevice ? { deviceId: { exact: selectedAudioDevice } } : true,
                });
                stream.getAudioTracks().forEach(track => track.enabled = !initialIsMuted);
                // Video track is already handled by the constraint, so no need to enable/disable here.
                setLocalStream(stream);
                localStreamRef.current = stream;

                const audioTrack = stream.getAudioTracks()[0];
                if (audioTrack) {
                    audioTransceiver.sender.replaceTrack(audioTrack);
                }

                const videoTrack = stream.getVideoTracks()[0];
                if (videoTrack && videoTransceiverRef.current) {
                    videoTransceiverRef.current.sender.replaceTrack(videoTrack);
                }

                pc.onicecandidate = (e) => {
                    if (!e.candidate) return;
                    const { sessionId, handleId } = janusSessionRef.current;
                    if (sessionId && handleId) {
                        sendTrickleCandidate(sessionId, handleId, e.candidate, token);
                    }
                };
                pc.onicegatheringstatechange = () => {
                    const { sessionId, handleId } = janusSessionRef.current;
                    if (pc.iceGatheringState === "complete" && sessionId && handleId) {
                        sendTrickleCompleted(sessionId, handleId, token);
                    }
                };

                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);

                const displayName = externalUserDetails
                    ? `${initialDisplayName}`
                    : `${currentUserInfo?.firstName} ${currentUserInfo?.lastName}`;
                const userId = currentUser?.uid;

                const res = await sendOffer(
                    displayName,
                    roomId!,
                    offer.sdp!,
                    token,
                    isMuted,
                    isCameraOn,
                    undefined,
                    userId
                );
                if (!res?.sdpAnswer) throw new Error("Failed to start publisher session.");

                await pc.setRemoteDescription({ type: "answer", sdp: res.sdpAnswer });
                janusSessionRef.current = {
                    sessionId: res.sessionId,
                    handleId: res.handleId,
                    feedId: res.feedId,
                };

                setJanusSessionId(res.sessionId);

                // Start keep-alive interval to maintain session (every 10 seconds)
                if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
                statsIntervalRef.current = setInterval(keepAliveAsync, 10000);
                
                // Start polling for participant changes as a backup to SignalR
                if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = setInterval(syncParticipants, 10000); // Poll every 10 seconds


                await connection.invoke(
                    "AnnouncePublisher",
                    roomId,
                    res.feedId,
                    displayName,
                    isMuted,
                    isCameraOn
                );

                // Fetch the definitive list of publishers and subscribe
                const publishers = await getPublishers(token, roomId);
                for (const publisher of publishers) {
                    if (publisher.feedId !== res.feedId) { // Don't subscribe to self
                        subscribeToRemoteFeed(publisher, res.sessionId);
                    }
                }

                if (res.sessionId && res.handleId && roomId) {
                    getHangupToken(res.sessionId, res.handleId, roomId, token).then(
                        (tokenResponse) => {
                            if (tokenResponse) hangupTokenRef.current = tokenResponse.hangupToken;
                        }
                    );
                }
                if (refreshSessionIntervalRef.current) clearInterval(refreshSessionIntervalRef.current);
                refreshSessionIntervalRef.current = setInterval(() => {
                    if (hubConnectionRef.current?.state === signalR.HubConnectionState.Connected && janusSessionRef.current.sessionId) {
                        // this is for updating the last used session
                        hubConnectionRef.current.invoke("RefreshSessionActivity", janusSessionRef.current.sessionId)
                            .catch(err => console.error("Failed to send session activity refresh:", err));
                    }
                }, 10000); // Ping every 10 seconds to keep the backend session alive


                {
                    // Call Initiate or Accept api 
                    await handleCallInitiation(
                        isPublisher,
                        relatedChatConversationId,
                        calenderEventId,
                        roomId,
                        currentUser,
                        externalUserDetails
                    );
                }


            } catch (error) {
                console.error("Error during media publishing:", error);
                alert("Failed to publish your video. Please check camera/mic permissions.");
            }
        };

        publish();

    }, [
        currentUser,
        roomId,
        navigate,
        initialDisplayName,
        initialVideoDevice,
        initialAudioDevice,
        currentUserInfo,
        cleanupRemoteFeed,
        checkNetworkStats,
        subscribeToRemoteFeed,
        initialIsMuted,
        initialIsCameraOn,
        externalUserDetails
    ]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            // Standard way to trigger the browser's confirmation dialog.
            event.preventDefault();
            event.returnValue = ""; // Required for legacy browsers.
        };

        // This function sends the hangup signal. It runs when the user *actually* leaves.
        const handlePageHide = () => {
            // Use the safe, single-use token for the main camera/mic feed
            if (hangupTokenRef.current) {
                const url = `${process.env.REACT_APP_API_BASE_URL}/video/hangup-beacon?hangupToken=${hangupTokenRef.current}`;
                navigator.sendBeacon(url);
                console.log("Beacon hangup sent for main session.");
            }
            // Use the safe, single-use token for the screen share feed
            if (screenShareHangupTokenRef.current) {
                const url = `${process.env.REACT_APP_API_BASE_URL}/video/hangup-beacon?hangupToken=${screenShareHangupTokenRef.current}`;
                navigator.sendBeacon(url);
                console.log("Beacon hangup sent for screen share session.");
            }
        };

        // Listen for the prompt event.
        window.addEventListener("beforeunload", handleBeforeUnload);
        // Listen for the final "leaving" event.
        window.addEventListener("pagehide", handlePageHide);

        // Cleanup: remove both listeners when the component unmounts.
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, []);

    useEffect(() => {
        // Initial start of the video room
        startVideoRoom();

        return () => {
            // This cleanup runs when the component unmounts.
            // The 'pagehide' event with sendBeacon is the primary mechanism for backend hangup on close.
            // This is a final safety net for local resources.
            cleanup();
        };
    }, [currentUser, roomId, cleanup, externalUserDetails?.tempSessionToken]); // Note: externalUserDetails should be a dependency if used in cleanup

    useEffect(() => {
        if (activeScreenShare && (!activeScreenShare.stream || !activeScreenShare.displayName)) {
            const screenFeed = remoteFeeds[activeScreenShare.feedId];
            if (screenFeed) {
                setActiveScreenShare(prev => {
                    if (!prev) return null;
                    const newStream = prev.stream || screenFeed.stream;
                    const newDisplayName = prev.displayName || screenFeed.displayName;
                    if (newStream !== prev.stream || newDisplayName !== prev.displayName) {
                        return { ...prev, stream: newStream, displayName: newDisplayName };
                    }
                    return prev;
                });
            }
        }
    }, [remoteFeeds, activeScreenShare]);

    const handleToggleMute = useCallback(() => {
        if (!localStreamRef.current || !hubConnectionRef.current || !roomId || !janusSessionRef.current.feedId) return;
        const newMutedState = !isMuted;
        localStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = !newMutedState;
        });
        setIsMuted(newMutedState);
        const { feedId } = janusSessionRef.current;

        if (feedId)
            hubConnectionRef.current.invoke(
                "BroadcastMediaState",
                roomId,
                feedId,
                newMutedState,
                isCameraOn
            );
    }, [isMuted, isCameraOn, roomId]);

    const handleToggleCamera = useCallback(async () => {
        // Guards
        if (!hubConnectionRef.current || !roomId || !janusSessionRef.current.feedId) return;
        if (isTogglingCameraRef.current) return; // ignore rapid/double clicks
        isTogglingCameraRef.current = true;

        try {
            const pc = publisherPcRef.current;
            if (!pc) return;

            const videoTransceiver = videoTransceiverRef.current;
            if (!videoTransceiver) {
                console.error("No video transceiver found to toggle camera.");
                return;
            }
            const sender = videoTransceiver.sender;

            const newCameraState = !isCameraOn;
            setIsCameraOn(newCameraState); // optimistic UI update

            if (newCameraState) {
                // Turning camera ON
                try {
                    const newVideoStream = await navigator.mediaDevices.getUserMedia({
                        video: selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true,
                    });
                    const newVideoTrack = newVideoStream.getVideoTracks()[0];
                    if (newVideoTrack) {
                        await sender.replaceTrack(newVideoTrack);

                        const stream = localStreamRef.current;
                        if (stream) {
                            stream.getVideoTracks().forEach(t => stream.removeTrack(t));
                            stream.addTrack(newVideoTrack);
                            setLocalStream(new MediaStream(stream.getTracks()));
                        } else {
                            localStreamRef.current = newVideoStream;
                            setLocalStream(newVideoStream);
                        }
                    } else {
                        throw new Error("No video track found in new stream.");
                    }
                } catch (error) {
                    console.error("Failed to get camera for toggling on:", error);
                    setIsCameraOn(false); // revert UI
                }
            } else {
                // Turning camera OFF
                await sender.replaceTrack(null);

                const stream = localStreamRef.current;
                if (stream) {
                    const videoTrack = stream.getVideoTracks()[0];
                    if (videoTrack) {
                        videoTrack.stop();
                        stream.removeTrack(videoTrack);
                        setLocalStream(new MediaStream(stream.getTracks()));
                    }
                }
            }

            // Broadcast the new state
            const { feedId } = janusSessionRef.current;
            if (feedId && roomId) {
                hubConnectionRef.current?.invoke(
                    "BroadcastMediaState",
                    roomId,
                    feedId,
                    isMuted,
                    newCameraState
                );
            }
        } finally {
            isTogglingCameraRef.current = false;
        }
    }, [isCameraOn, isMuted, roomId, selectedVideoDevice]);

    const handleChangeDevice = useCallback(
        async (kind: "video" | "audio", deviceId: string) => {
            if (!localStreamRef.current || !publisherPcRef.current) {
                console.warn(
                    "No local stream or publisher PC available for device change."
                );
                return;
            }

            let newVideoDeviceId = selectedVideoDevice;
            let newAudioDeviceId = selectedAudioDevice;

            if (kind === "video") {
                newVideoDeviceId = deviceId;
            } else {
                newAudioDeviceId = deviceId;
            }

            try {
                localStreamRef.current.getTracks().forEach((track) => track.stop());

                const newStream = await navigator.mediaDevices.getUserMedia({
                    video: newVideoDeviceId
                        ? { deviceId: { exact: newVideoDeviceId } }
                        : true,
                    audio: newAudioDeviceId
                        ? { deviceId: { exact: newAudioDeviceId } }
                        : true,
                });

                localStreamRef.current = newStream;
                setLocalStream(newStream);

                const videoSender = publisherPcRef.current
                    .getSenders()
                    .find((s) => s.track?.kind === "video");
                const audioSender = publisherPcRef.current
                    .getSenders()
                    .find((s) => s.track?.kind === "audio");

                if (newStream.getVideoTracks().length > 0 && videoSender) {
                    await videoSender.replaceTrack(newStream.getVideoTracks()[0]);
                }
                if (newStream.getAudioTracks().length > 0 && audioSender) {
                    await audioSender.replaceTrack(newStream.getAudioTracks()[0]);
                }

                if (kind === "video") {
                    setSelectedVideoDevice(deviceId);
                } else {
                    setSelectedAudioDevice(deviceId);
                }

                console.log(`Successfully changed ${kind} device to: ${deviceId}`);
            } catch (error) {
                console.error(`Error changing ${kind} device to ${deviceId}:`, error);
            }
        },
        [selectedVideoDevice, selectedAudioDevice, isMuted, isCameraOn, roomId]
    );

    const stopScreenSharing = useCallback(async () => {
        const { sessionId, handleId } = janusScreenShareHandleRef.current;
        if (!sessionId || !handleId || !roomId) return;

        try {
            const token = currentUser ? await currentUser.getIdToken() : "";
            await hangup(sessionId, handleId, roomId, token, true);

            if (hubConnectionRef.current?.state === signalR.HubConnectionState.Connected) {
                try {
                    await hubConnectionRef.current.invoke("AnnounceScreenShareStopped", roomId);
                } catch (invokeError: any) {
                    // Ignore errors related to connection being closed
                    if (invokeError?.message?.includes("connection being closed") || 
                        invokeError?.message?.includes("Invocation canceled") ||
                        invokeError?.message?.includes("underlying connection")) {
                        console.log("SignalR connection closed during screen share stop - this is expected");
                    } else {
                        console.error("Error announcing screen share stopped:", invokeError);
                    }
                }
            }
        } catch (error) {
            console.error("Error stopping screen share:", error);
        }

        // Clean up only screen share resources
        screenSharePublisherPcRef.current?.close();
        screenShareStreamRef.current?.getTracks().forEach((track) => track.stop());
        screenSharePublisherPcRef.current = null;
        screenShareStreamRef.current = null;
        janusScreenShareHandleRef.current = { sessionId: null, handleId: null, feedId: null };
        screenShareHangupTokenRef.current = null;

        setActiveScreenShare(null);
        setIsSharingScreen(false);

    }, [currentUser, roomId]);

    const handleShareScreen = useCallback(async () => {
        if (isSharingScreen) {
            stopScreenSharing();
            return;
        }
        if (
            !hubConnectionRef.current ||
            (!currentUser && !externalUserDetails) ||
            !janusSessionRef.current.sessionId || // Ensure main session is active
            !roomId
        )
            return;

        let stream: MediaStream | null = null;
        try {
            stream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: true,
            });

            if (!stream || stream.getVideoTracks().length === 0) {
                console.warn(
                    "Screen share stream obtained, but no video tracks found. User might have cancelled or no screen selected."
                );
                stream?.getTracks().forEach((track) => track.stop());
                return;
            }
        } catch (error: any) {
            if (
                error.name === "NotAllowedError" ||
                error.name === "AbortError" ||
                error.name === "NotFoundError"
            ) {
                console.log(
                    "Screen sharing cancelled by user or no display found:",
                    error.message
                );
                return;
            }
            console.error("Error getting display media:", error);
            return;
        }

        const permissionGranted = await hubConnectionRef.current.invoke(
            "RequestToShareScreen",
            roomId
        );
        if (!permissionGranted) {
            alert("Someone else is already sharing their screen.");
            stream.getTracks().forEach((track) => track.stop());
            return;
        }

        const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
        screenSharePublisherPcRef.current = pc;
        screenShareStreamRef.current = stream;

        const videoTrack = stream.getVideoTracks()[0];
        const audioTrack = stream.getAudioTracks()[0];

        if (videoTrack) pc.addTransceiver(videoTrack, { direction: "sendonly" });
        if (audioTrack) pc.addTransceiver(audioTrack, { direction: "sendonly" });

        const token = currentUser ? await currentUser.getIdToken() : "";
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const displayName = externalUserDetails
            ? `${initialDisplayName} (Screen)`
            : `${currentUserInfo?.firstName} ${currentUserInfo?.lastName} (Screen)`;
        const userId = currentUser?.uid;
        // Pass the existing main session ID for screen share
        const res = await sendOffer(
            displayName,
            roomId,
            pc.localDescription!.sdp!,
            token,
            isMuted,
            isCameraOn,
            janusSessionRef.current.sessionId! // Pass existing sessionId
        );

        if (!res?.sdpAnswer || !res.handleId || !res.feedId) {
            throw new Error("Failed to get valid response for screen share.");
        }
        janusScreenShareHandleRef.current = {
            sessionId: janusSessionRef.current.sessionId, // Use the main session ID
            handleId: res.handleId,
            feedId: res.feedId,
        };

        pc.onicecandidate = (e) => {
            if (!e.candidate) return;
            const sess = janusSessionRef.current.sessionId;
            const handle = janusScreenShareHandleRef.current.handleId;
            if (sess && handle) {
                sendTrickleCandidate(sess, handle, e.candidate, token);
            }
        };

        // end-of-candidates for screen-share handle
        pc.onicegatheringstatechange = () => {
            const sess = janusSessionRef.current.sessionId;
            const handle = janusScreenShareHandleRef.current.handleId;
            if (pc.iceGatheringState === "complete" && sess && handle) {
                sendTrickleCompleted(sess, handle, token);
            }
        };

        if (janusSessionRef.current.sessionId && res.handleId && roomId && token) {
            const tokenResponse = await getHangupToken(
                janusSessionRef.current.sessionId,
                res.handleId,
                roomId,
                token
            );
            if (tokenResponse) {
                screenShareHangupTokenRef.current = tokenResponse.hangupToken;
                console.log(
                    "Acquired single-use hangup token for screen share session."
                );
            } else {
                console.warn(
                    "Could not acquire hangup token for screen share session."
                );
            }
        }

        await pc.setRemoteDescription({ type: "answer", sdp: res.sdpAnswer });
        await hubConnectionRef.current?.invoke(
            "AnnounceScreenShareStarted",
            roomId,
            res.feedId,
            displayName
        );
        setActiveScreenShare({ feedId: res.feedId, stream: stream, userId, displayName });
        setIsSharingScreen(true);

        stream.getVideoTracks()[0].onended = () => stopScreenSharing();
    }, [
        isSharingScreen,
        stopScreenSharing,
        currentUser,
        roomId,
        currentUserInfo,
        janusSessionRef.current.sessionId, // Add dependency for existing session ID
    ]);

    const performIceRestart = useCallback(
        async (
            pc: RTCPeerConnection,
            sessionId: number,
            handleId: number,
            isPublisher: boolean,
            feedIdToResubscribe?: string
        ) => {
            if (!currentUser && !externalUserDetails) return;
            const token = currentUser ? await currentUser.getIdToken() : "";
            try {
                pc.restartIce();

                const offer = await pc.createOffer({ iceRestart: true });
                await pc.setLocalDescription(offer);

                let res;
                if (isPublisher) {
                    const displayName = externalUserDetails
                        ? `${initialDisplayName}`
                        : `${currentUserInfo?.firstName} ${currentUserInfo?.lastName}`;
                    res = await sendOffer(
                        displayName,
                        roomId!,
                        offer.sdp!,
                        token,
                        isMuted,
                        isCameraOn,
                        sessionId
                    ); // Pass sessionId here
                } else {
                    return;
                }

                if (!res?.sdpAnswer) {
                    throw new Error("Failed to get valid SDP answer during ICE restart.");
                }

                await pc.setRemoteDescription({ type: "answer", sdp: res.sdpAnswer });
                console.log("ICE restart successful.");

                if (
                    isPublisher &&
                    isSharingScreen &&
                    janusScreenShareHandleRef.current.feedId
                ) {
                }
            } catch (error) {
                console.error("Error during ICE restart:", error);
                throw error; // Re-throw to be caught by the caller (e.g., onconnectionstatechange)
            }
        },
        [
            currentUser,
            currentUserInfo,
            roomId,
            isSharingScreen,
            janusSessionRef.current.sessionId, // Added dependency
        ]
    );

    const handleHangUp = useCallback(async (isCallRejectedHangup: boolean = false) => {
        const { sessionId, handleId } = janusSessionRef.current;
        if (sessionId && handleId && roomId) {
            try {
                NotifyCallRingingOff();
                const token = currentUser ? await currentUser.getIdToken() : "";
                // Hang up main feed
                await hangup(sessionId, handleId, roomId, token, false, relatedChatConversationId, isCallRejectedHangup);

                // Hang up screen share feed if it exists
                const { sessionId: screenSessionId, handleId: screenHandleId } = janusScreenShareHandleRef.current;
                if (screenSessionId && screenHandleId) {
                    await hangup(screenSessionId, screenHandleId, roomId, token, true);
                }

                // Notify hub
                if (hubConnectionRef.current?.state === signalR.HubConnectionState.Connected) {
                    try {
                        await hubConnectionRef.current.invoke("NotifyUserLeft", roomId);
                    } catch (invokeError: any) {
                        // Ignore errors related to connection being closed - this is expected during cleanup
                        if (invokeError?.message?.includes("connection being closed") || 
                            invokeError?.message?.includes("Invocation canceled") ||
                            invokeError?.message?.includes("underlying connection")) {
                            console.log("SignalR connection closed during hangup - this is expected");
                        } else {
                            console.error("Error notifying hub of user leaving:", invokeError);
                        }
                    }
                }
            } catch (error) {
                console.error("Error during graceful hangup:", error);
            }
        }

        await cleanup();

        if (relatedChatConversationId) {
            navigate(`/chat/${relatedChatConversationId}`);
        } else if(externalUserDetails) {
            window.close();
        } else {
            navigate("/home");
        }
    }, [currentUser, roomId, navigate, cleanup, relatedChatConversationId, externalUserDetails]);

    const NotifyCallRingingOff = async () => {debugger
        if (hubConnectionRef.current && roomId && relatedChatConversationId && isPublisher) {
            try {
                await hubConnectionRef.current.invoke(
                    "NotifyCallRingingOffToUsers",
                    roomId,
                    relatedChatConversationId
                );

                console.log("Notifies");
                
            } catch (err) {
                console.error("Failed to notify call ringing off via SignalR:", err);
            }
        }
    }

    return {
        localStream,
        remoteFeeds,
        isMuted,
        isCameraOn,
        isSharingScreen,
        activeScreenShare,
        selectedAudioDevice,
        selectedVideoDevice,
        localParticipant: {
            isLocal: true,
            feedId: janusSessionRef.current.feedId || "local",
            userId: externalUserDetails ? externalUserDetails.externalUserDetailId : currentUser?.uid,
            displayName: externalUserDetails
                ? `${initialDisplayName} (You)`
                : `${currentUserInfo?.firstName} ${currentUserInfo?.lastName} (You)`,
            stream: localStream,
            isMuted,
            isCameraOn,
            networkQuality: networkQuality,
            latency: latency,
        },
        actions: {
            handleToggleMute,
            handleToggleCamera,
            handleChangeDevice,
            handleHangUp,
            handleShareScreen,
        },
    };

    async function handleCallInitiation(
        isPublisher: boolean,
        relatedChatConversationId: number | undefined,
        calenderEventId: string | null,
        roomId: string | undefined,
        currentUser?: User | null,
        externalUserDetails?: ValidateOtpResponseDto | null,
    ) {
        const token = currentUser ? await currentUser.getIdToken() : "";
        if (isPublisher) {
            const isPublisherFromLocal = localStorage.getItem("isPublisher");
            if (isPublisherFromLocal == "true") {
                const payload: InitiateCallRequest = {
                    targetUserId: null,
                    relatedChatConversationId,
                    calenderEventId,
                    roomId: roomId ?? "",
                };
                if (token) {
                    try {
                        await InitiateVideoCallNotifyAsync(token, payload);
                        localStorage.removeItem("isPublisher");
                    } catch (err) {
                        console.error("Call initiation failed", err);
                    }
                }
            }

        } else {
            const payload: CallActionRequest = {
                roomId: roomId ?? "",
                conversationId: relatedChatConversationId
            };
            try {
                await AcceptVideoCallAsync(token, payload);
            } catch (err) {
                console.error("Accept call failed", err);
            }
        }
    }
};